import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Eye,
  EyeOff,
  AlertCircle,
  Check,
  ChevronDown,
  Gift,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { getStoredRef } from "../components/RefCapture";
import api from "../lib/api";

const REFERRAL_OPTIONS = [
  { value: "facebook", label: "Facebook" },
  { value: "instagram", label: "Instagram" },
  { value: "tiktok", label: "TikTok" },
  { value: "google", label: "Google" },
  { value: "affiliate", label: "Affiliate / Referral" },
];

const Signup = () => {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [referralSource, setReferralSource] = useState("");
  const [pwd, setPwd] = useState("");
  const [show, setShow] = useState(false);
  const [agree, setAgree] = useState(false);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refCode, setRefCode] = useState("");
  const [sponsor, setSponsor] = useState(null);

  useEffect(() => {
    const code = (params.get("ref") || getStoredRef() || "").trim().toUpperCase();
    if (!code) return;
    setRefCode(code);
    setReferralSource("affiliate");
    api.get(`/affiliate/ref/${encodeURIComponent(code)}`)
      .then(({ data }) => { if (data.valid) setSponsor(data.sponsor_name); })
      .catch(() => {});
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!agree) {
      setErr("Please accept the Terms to continue.");
      return;
    }
    if (pwd.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    if (!phone.trim() || phone.replace(/\D/g, "").length < 5) {
      setErr("Please enter a valid phone number.");
      return;
    }
    if (!referralSource) {
      setErr("Please tell us how you found out about us.");
      return;
    }
    setLoading(true);
    try {
      await signup({
        email: email.trim(),
        password: pwd,
        name: name.trim(),
        phone: phone.trim(),
        referral_source: referralSource,
        ref_code: refCode || getStoredRef() || undefined,
      });
      nav("/dashboard", { replace: true });
    } catch (e2) {
      setErr(
        e2?.response?.data?.detail ||
          "Sign up failed. Try a different email."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bb-bg text-white flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-30 pointer-events-none"></div>
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[500px] bg-gradient-to-br from-[#1c3df5]/20 to-transparent blur-3xl pointer-events-none"></div>
      <div className="relative w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="signup-logo-link">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#3b59ff] to-[#1c3df5] flex items-center justify-center">
            <div className="w-3 h-3 rounded-full bg-white/95"></div>
          </div>
          <span className="font-display font-bold text-2xl tracking-tight">Northwind</span>
        </Link>
        <div className="bb-card rounded-3xl p-7 lg:p-8" data-testid="signup-card">
          <h1 className="font-display font-bold text-[26px] text-white mb-1">Create your account</h1>
          <p className="text-[13px] text-white/55 mb-6">Start trading and copying top traders in minutes.</p>
          {refCode && (
            <div className="flex items-start gap-2.5 mb-5 px-3.5 py-3 rounded-xl bg-[#1c3df5]/12 border border-[#1c3df5]/30" data-testid="referral-banner">
              <Gift size={16} className="text-[#7d8eff] shrink-0 mt-0.5" />
              <div className="text-[12.5px] leading-snug">
                <span className="text-white font-medium">
                  {sponsor ? `You were invited by ${sponsor} 🎉` : "Referral applied 🎉"}
                </span>
                <div className="text-white/55">
                  Referral code <span className="font-mono text-white/80">{refCode}</span> is attached to your sign-up.
                </div>
              </div>
            </div>
          )}
          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <div>
              <label className="text-[12px] text-white/55 mb-1.5 block">Full name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="Alex Morgan"
                data-testid="signup-name-input"
                className="w-full h-11 px-4 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="text-[12px] text-white/55 mb-1.5 block">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
                data-testid="signup-email-input"
                className="w-full h-11 px-4 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="text-[12px] text-white/55 mb-1.5 block">Phone number</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                autoComplete="tel"
                placeholder="+44 7400 123456"
                data-testid="signup-phone-input"
                className="w-full h-11 px-4 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
              />
            </div>
            <div>
              <label className="text-[12px] text-white/55 mb-1.5 block">
                How did you find out about us?
              </label>
              <div className="relative">
                <select
                  value={referralSource}
                  onChange={(e) => setReferralSource(e.target.value)}
                  required
                  data-testid="signup-referral-select"
                  className="appearance-none w-full h-11 pl-4 pr-10 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#1c3df5]"
                >
                  <option value="" disabled className="bg-[#0c0f1c]">
                    Select an option…
                  </option>
                  {REFERRAL_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value} className="bg-[#0c0f1c]">
                      {o.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={15}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 pointer-events-none"
                />
              </div>
            </div>
            <div>
              <label className="text-[12px] text-white/55 mb-1.5 block">Password</label>
              <div className="relative">
                <input
                  type={show ? "text" : "password"}
                  value={pwd}
                  onChange={(e) => setPwd(e.target.value)}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  placeholder="At least 6 characters"
                  data-testid="signup-password-input"
                  className="w-full h-11 px-4 pr-10 bg-white/[0.02] border border-white/10 rounded-xl text-[14px] text-white outline-none focus:border-[#1c3df5] placeholder:text-white/30"
                />
                <button
                  type="button"
                  onClick={() => setShow(!show)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 hover:text-white"
                  tabIndex={-1}
                  data-testid="signup-password-toggle"
                >
                  {show ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <label className="flex items-start gap-2 text-[12px] text-white/65 cursor-pointer">
              <span
                onClick={() => setAgree(!agree)}
                data-testid="signup-terms-checkbox"
                className={`mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                  agree ? "bg-[#1c3df5] border-[#1c3df5]" : "border-white/25"
                }`}
              >
                {agree && <Check size={11} className="text-white" />}
              </span>
              <span>
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="text-[#7d8eff] hover:underline">Terms of Service</Link>,{" "}
                <Link to="/privacy" target="_blank" className="text-[#7d8eff] hover:underline">Privacy Policy</Link>{" "}
                and acknowledge the{" "}
                <Link to="/risk-disclosure" target="_blank" className="text-[#7d8eff] hover:underline">Risk Disclosure</Link>.
              </span>
            </label>
            {err && (
              <div
                data-testid="signup-error"
                className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-[12px] text-rose-300"
              >
                <AlertCircle size={14} className="shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit-button"
              className="w-full h-11 rounded-xl bb-blue text-white font-medium text-[14px] inline-flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? "Creating account..." : (<>Create account <ArrowRight size={15} /></>)}
            </button>
          </form>
          <div className="mt-6 pt-5 border-t border-white/5 text-center text-[13px] text-white/55">
            Already have an account?{" "}
            <Link to="/login" className="text-[#7d8eff] hover:underline font-medium" data-testid="signup-to-login-link">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
